import React, { useState, useEffect } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { getFirestore, collection, onSnapshot, addDoc, serverTimestamp, doc, deleteDoc, updateDoc, query, where, writeBatch, getDocs } from 'firebase/firestore';
import { format, parseISO, isSameDay, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, subMonths, isToday, addYears, addWeeks } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CalendarRange, Clock, User, Clipboard, AlertCircle, CheckCircle, Trash2, ChevronLeft, ChevronRight, Edit, LogIn, LogOut, UserPlus, Repeat, Home, X } from 'lucide-react';

// Variáveis de configuração do seu projeto no Firebase.
// Por favor, substitua estas informações pelas credenciais do seu projeto.
const firebaseConfig = {
    apiKey: "AIzaSyCSaz7hssScn1SxAFanfYLPrGyLSIgNiYY",
    authDomain: "agenda-apartamento-acea.firebaseapp.com",
    projectId: "agenda-apartamento-acea",
    storageBucket: "agenda-apartamento-acea.firebasestorage.app",
    messagingSenderId: "975156347377",
    appId: "1:975156347377:web:6d6e15bb9baf1864e6c450"
};

// A variável 'appId' agora usa o 'projectId' do seu objeto de configuração do Firebase.
const appId = firebaseConfig.projectId;

// Configuração inicial do Firebase e Firestore
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const auth = getAuth(app);

// Componente principal da aplicação
const App = () => {
    const [bookings, setBookings] = useState([]);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [name, setName] = useState('');
    const [activity, setActivity] = useState('');
    const [space, setSpace] = useState('');
    const [userId, setUserId] = useState(null);
    const [userEmail, setUserEmail] = useState('');
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('warning');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditingModalOpen, setIsEditingModalOpen] = useState(false);
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [editingBooking, setEditingBooking] = useState(null);
    const [editedName, setEditedName] = useState('');
    const [editedActivity, setEditedActivity] = useState('');
    const [editedSpace, setEditedSpace] = useState('');
    const [editedStartDate, setEditedStartDate] = useState('');
    const [editedEndDate, setEditedEndDate] = useState('');
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(new Date());

    // Novos estados para recorrência
    const [isRecurrent, setIsRecurrent] = useState(false);
    const [recurrenceType, setRecurrenceType] = useState('Semanal');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [bookingToDelete, setBookingToDelete] = useState(null);
    const [isEditActionModalOpen, setIsEditActionModalOpen] = useState(false); // Novo modal para ação de edição

    // Estados para o formulário de autenticação
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const isSigningUp = false; // Fixo para login

    // Efeito para inicializar o Firebase Auth e Firestore
    useEffect(() => {
        const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
            if (user) {
                setUserId(user.uid);
                setUserEmail(user.email);
                setIsLoggedIn(user.email !== null);
                setIsAdmin(user.email === 'admin@example.com');
            } else {
                setUserId(null);
                setUserEmail('');
                setIsLoggedIn(false);
                setIsAdmin(false);
            }
            setLoading(false);
        });
        setLoading(false);

        return () => unsubscribeAuth();
    }, []);

    // Efeito para buscar as reservas em tempo real (onSnapshot)
    useEffect(() => {
        const bookingsCollectionPath = `artifacts/${appId}/public/data/apartmentBookings`;
        const q = collection(db, bookingsCollectionPath);

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const bookingsList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                start: doc.data().start?.toDate(),
                end: doc.data().end?.toDate(),
            }));
            bookingsList.sort((a, b) => a.start - b.start);
            setBookings(bookingsList);
        }, (error) => {
            console.error("Erro ao buscar reservas:", error);
        });

        return () => unsubscribe();
    }, []);

    // Funções de navegação do calendário
    const handlePrevMonth = () => {
        setCurrentMonth(subMonths(currentMonth, 1));
    };

    const handleNextMonth = () => {
        setCurrentMonth(addMonths(currentMonth, 1));
    };

    // Funções do modal de mensagens
    const showMessage = (msg, type = null) => {
        let resolvedType = type;
        if (!resolvedType) {
            const lower = msg.toLowerCase();
            if (lower.includes('sucesso') || lower.includes('você saiu')) {
                resolvedType = 'success';
            } else if (lower.includes('erro') || lower.includes('falha')) {
                resolvedType = 'error';
            } else {
                resolvedType = 'warning';
            }
        }
        setMessage(msg);
        setMessageType(resolvedType);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setMessage('');
        setMessageType('warning');
    };

    // Funções do modal de edição
    const openEditingModal = (booking) => {
        if (booking.userId !== userId && !isAdmin) return;
        setEditingBooking(booking);
        setEditedName(booking.name);
        setEditedActivity(booking.activity);
        setEditedSpace(booking.space || '');
        setEditedStartDate(format(booking.start, "yyyy-MM-dd'T'HH:mm"));
        setEditedEndDate(format(booking.end, "yyyy-MM-dd'T'HH:mm"));
        setIsEditingModalOpen(true);
        setIsEditActionModalOpen(false); // Garante que o modal de ação esteja fechado
    };

    const closeEditingModal = () => {
        setEditingBooking(null);
        setIsEditingModalOpen(false);
        setIsEditActionModalOpen(false);
    };

    // Funções do modal de login
    const closeLoginModal = () => {
        setIsLoginModalOpen(false);
        setEmail('');
        setPassword('');
    };

    // Funções do modal de exclusão
    const openDeleteModal = (booking) => {
        if (booking.userId !== userId && !isAdmin) return;
        setBookingToDelete(booking);
        setIsDeleteModalOpen(true);
    };

    const closeDeleteModal = () => {
        setBookingToDelete(null);
        setIsDeleteModalOpen(false);
    };

    // Lógica de verificação de conflitos / validação de datas
    const checkConflict = (newStart, newEnd) => {
        const newStartDate = parseISO(newStart);
        const newEndDate = parseISO(newEnd);

        if (newStartDate <= new Date()) {
            showMessage("A data de início da reserva não pode ser no passado.");
            return true;
        }

        if (newStartDate >= newEndDate) {
            showMessage("A data de início deve ser anterior à data de término.");
            return true;
        }

        // Trava de agendamento no mesmo dia e horário removida para permitir que múltiplos espaços sejam agendados simultaneamente
        return false;
    };

    // Funções de autenticação
    const handleSignIn = async (e) => {
        e.preventDefault();
        try {
            await signInWithEmailAndPassword(auth, email, password);
            setIsLoginModalOpen(false);
            setEmail('');
            setPassword('');
            showMessage("Login realizado com sucesso!", "success");
        } catch (error) {
            showMessage("Erro ao fazer login: " + error.message, "error");
        }
    };

    const handleSignOut = async () => {
        try {
            await signOut(auth);
            showMessage("Você saiu da sua conta.", "success");
        } catch (error) {
            showMessage("Erro ao sair: " + error.message, "error");
        }
    };

    // Função auxiliar para calcular a próxima data recorrente
    const getNextRecurrence = (currentDate, type) => {
        switch (type) {
            case 'Semanal':
                return addWeeks(currentDate, 1);
            case 'Mensal':
                return addMonths(currentDate, 1);
            case 'Anual':
                return addYears(currentDate, 1);
            default:
                return null;
        }
    };

    // Função para lidar com o envio de um novo agendamento (incluindo recorrência)
    const handleBookingSubmit = async (e) => {
        e.preventDefault();
        if (!startDate || !endDate || !name || !activity || !space.trim()) {
            showMessage("Por favor, preencha todos os campos: nome, atividade, espaço e as datas.");
            return;
        }

        if (checkConflict(startDate, endDate)) {
            return;
        }

        try {
            const bookingsCollectionPath = `artifacts/${appId}/public/data/apartmentBookings`;
            const durationMs = parseISO(endDate).getTime() - parseISO(startDate).getTime();
            const batch = writeBatch(db);
            const recurrenceId = isRecurrent ? Date.now().toString() : null;

            let currentStart = parseISO(startDate);
            let currentEnd = parseISO(endDate);
            const limitDate = addYears(new Date(), 1); // Limite de 1 ano para recorrências

            do {
                const newBookingData = {
                    start: currentStart,
                    end: currentEnd,
                    name: name,
                    activity: activity,
                    space: space.trim(),
                    userId: userId,
                    userEmail: userEmail,
                    createdAt: serverTimestamp(),
                    recurrenceId: recurrenceId, // Adiciona o ID de recorrência
                };

                const docRef = doc(collection(db, bookingsCollectionPath));
                batch.set(docRef, newBookingData);

                if (!isRecurrent) {
                    break; // Sai do loop se não for recorrente
                }

                currentStart = getNextRecurrence(currentStart, recurrenceType);
                currentEnd = new Date(currentStart.getTime() + durationMs);

            } while (currentStart < limitDate);

            await batch.commit();

            setStartDate('');
            setEndDate('');
            setName('');
            setActivity('');
            setSpace('');
            setIsRecurrent(false);
            setRecurrenceType('Semanal');

            showMessage(isRecurrent ? "Reservas recorrentes adicionadas com sucesso!" : "Reserva adicionada com sucesso!");

        } catch (error) {
            console.error("Erro ao adicionar reserva:", error);
            showMessage("Ocorreu um erro ao tentar adicionar a reserva.");
        }
    };

    // Função para lidar com a edição de um agendamento - Ação Principal do formulário
    const handleEditFormSubmit = (e) => {
        e.preventDefault();
        if (!editedStartDate || !editedEndDate || !editedName || !editedActivity || !editedSpace.trim()) {
            showMessage("Por favor, preencha todos os campos: nome, atividade, espaço e as datas.");
            return;
        }
        if (checkConflict(editedStartDate, editedEndDate)) {
            return;
        }

        // Se for recorrente, abre o modal de ação. Caso contrário, salva direto.
        if (editingBooking.recurrenceId) {
            setIsEditActionModalOpen(true);
        } else {
            handleFinalEditSubmit('single');
        }
    };

    // Função final para submeter a edição ao Firestore
    const handleFinalEditSubmit = async (editOption) => {
        try {
            const bookingsCollectionPath = `artifacts/${appId}/public/data/apartmentBookings`;
            const batch = writeBatch(db);
            const bookingToEdit = editingBooking;

            // Dados da nova instância (duração e conteúdo)
            const oldStart = bookingToEdit.start.getTime();
            const oldEnd = bookingToEdit.end.getTime();
            const newStart = parseISO(editedStartDate);
            const newEnd = parseISO(editedEndDate);
            const durationChangeMs = (newEnd.getTime() - newStart.getTime()) - (oldEnd - oldStart);

            const updatedFields = {
                name: editedName,
                activity: editedActivity,
                space: editedSpace.trim(),
            };

            if (editOption === 'single') {
                // 1. Salvar Apenas Este Evento
                const docRef = doc(db, bookingsCollectionPath, bookingToEdit.id);
                batch.update(docRef, {
                    ...updatedFields,
                    start: newStart,
                    end: newEnd,
                });
                showMessage("Reserva individual atualizada com sucesso!");

            } else if (editOption === 'future') {
                // 2. Salvar Este e Todos os Futuros
                const recurrenceId = bookingToEdit.recurrenceId;

                // 2a. Atualizar o evento atual
                const currentDocRef = doc(db, bookingsCollectionPath, bookingToEdit.id);
                batch.update(currentDocRef, {
                    ...updatedFields,
                    start: newStart,
                    end: newEnd,
                });

                // 2b. Buscar e atualizar instâncias futuras
                const q = query(
                    collection(db, bookingsCollectionPath),
                    where('recurrenceId', '==', recurrenceId),
                );
                const snapshot = await getDocs(q);

                const startTimeToCompare = newStart.getTime(); // Começa a partir da nova hora do evento atual

                snapshot.docs.forEach(docSnapshot => {
                    const docStartTime = docSnapshot.data().start.toDate().getTime();

                    // Filtra no cliente: APENAS instâncias ESTRICTAMENTE FUTURAS
                    if (docStartTime > oldStart) {
                        const newFutureStartTime = new Date(docStartTime + (newStart.getTime() - oldStart));
                        const newFutureEndTime = new Date(newFutureStartTime.getTime() + (newEnd.getTime() - newStart.getTime()));

                        batch.update(docSnapshot.ref, {
                            ...updatedFields,
                            start: newFutureStartTime,
                            end: newFutureEndTime,
                        });
                    }
                });
                showMessage("Reservas futura e a atual atualizadas com sucesso!");
            }

            await batch.commit();
            closeEditingModal();
        } catch (error) {
            console.error("Erro ao atualizar reserva:", error);
            showMessage("Ocorreu um erro ao tentar atualizar a reserva.");
        }
    };


    // Função principal de deleção (chamada pelo modal)
    const handleRecurrenceDelete = async (deleteOption) => {
        const bookingId = bookingToDelete.id;
        const recurrenceId = bookingToDelete.recurrenceId;
        const bookingsCollectionPath = `artifacts/${appId}/public/data/apartmentBookings`;

        try {
            const batch = writeBatch(db);

            if (deleteOption === 'single') {
                // Deletar apenas a instância atual
                const docRef = doc(db, bookingsCollectionPath, bookingId);
                batch.delete(docRef);
                showMessage("Reserva individual cancelada com sucesso!");
            } else if (deleteOption === 'future') {
                // Deletar a instância atual e todas as futuras
                if (!recurrenceId) {
                    const docRef = doc(db, bookingsCollectionPath, bookingId);
                    batch.delete(docRef);
                } else {
                    const q = query(
                        collection(db, bookingsCollectionPath),
                        where('recurrenceId', '==', recurrenceId),
                    );
                    const snapshot = await getDocs(q);

                    const startTimeToDelete = bookingToDelete.start.getTime();

                    snapshot.docs.forEach(doc => {
                        const docStartTime = doc.data().start.toDate().getTime();

                        // Inclui o evento atual e todos os futuros (>=)
                        if (docStartTime >= startTimeToDelete) {
                            batch.delete(doc.ref);
                        }
                    });
                }
                showMessage("Reservas futuras e a atual canceladas com sucesso!");
            }

            await batch.commit();
            closeDeleteModal();
        } catch (error) {
            console.error("Erro ao deletar reserva(s):", error);
            showMessage("Ocorreu um erro ao tentar cancelar a(s) reserva(s).");
        }
    };


    // Lógica do calendário
    const startOfCurrentMonth = startOfMonth(currentMonth);
    const endOfCurrentMonth = endOfMonth(currentMonth);
    const daysInMonth = eachDayOfInterval({ start: startOfCurrentMonth, end: endOfCurrentMonth });

    const firstDayOfWeek = daysInMonth[0].getDay();
    const leadingDays = Array.from({ length: firstDayOfWeek }, (_, i) => i);

    const filteredBookings = bookings.filter(booking => isSameDay(booking.start, selectedDate));

    // Função para obter as reservas de um dia específico
    const getBookingsForDay = (day) => {
        return bookings.filter(booking => isSameDay(booking.start, day));
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-100 p-4">
                <div className="text-center p-8 bg-white rounded-xl shadow-lg border border-gray-200">
                    <p className="text-xl font-bold text-gray-700">Carregando...</p>
                    <p className="text-sm text-gray-500 mt-2">Iniciando a aplicação de agendamento.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex items-start justify-center min-h-screen bg-gray-100 p-4 font-sans text-gray-800">
            <div className="w-full max-w-4xl">
                {/* Cabeçalho da aplicação */}
                <header className="bg-white rounded-t-xl shadow-lg border border-gray-200 p-6 mb-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div>
                            <h1 className="text-3xl font-extrabold text-indigo-800 mb-2">Agendamento do Apartamento</h1>
                            <p className="text-gray-600">
                                Gerencie as reservas e espaços de uso do apartamento de forma simples e flexível.
                            </p>
                            {isLoggedIn ? (
                                <p className="mt-3 text-sm font-semibold text-gray-500">
                                    Logado como: <span className="text-indigo-600 break-words">{userEmail}</span>
                                    {isAdmin && <span className="ml-2 px-2 py-1 bg-indigo-200 text-indigo-800 text-xs font-bold rounded-full">Admin</span>}
                                </p>
                            ) : (
                                <p className="mt-3 text-sm font-semibold text-gray-500">
                                    Faça login para gerenciar as reservas.
                                </p>
                            )}
                        </div>
                        <div className="flex items-center">
                            {isLoggedIn ? (
                                <button
                                    onClick={handleSignOut}
                                    className="flex items-center space-x-1 px-4 py-2 bg-red-500 text-white font-semibold rounded-md shadow hover:bg-red-600 transition-colors"
                                >
                                    <LogOut size={16} />
                                    <span>Sair</span>
                                </button>
                            ) : (
                                <button
                                    onClick={() => setIsLoginModalOpen(true)}
                                    className="flex items-center space-x-2 px-5 py-2.5 bg-indigo-600 text-white font-bold rounded-md shadow-md hover:bg-indigo-700 transition-all transform hover:scale-105"
                                >
                                    <LogIn size={18} />
                                    <span>Login</span>
                                </button>
                            )}
                        </div>
                    </div>
                </header>

                {/* Calendário e lista de reservas sempre visíveis */}
                <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 mb-6">
                    {/* Calendário */}
                    <div className="mb-6">
                        <div className="flex justify-between items-center mb-4">
                            <button onClick={handlePrevMonth} className="p-2 rounded-full text-indigo-600 hover:bg-gray-200 transition-colors">
                                <ChevronLeft size={24} />
                            </button>
                            <h3 className="text-2xl font-bold text-gray-700">
                                {format(currentMonth, "MMMM yyyy", { locale: ptBR })}
                            </h3>
                            <button onClick={handleNextMonth} className="p-2 rounded-full text-indigo-600 hover:bg-gray-200 transition-colors">
                                <ChevronRight size={24} />
                            </button>
                        </div>
                        <div className="grid grid-cols-7 gap-1 text-center font-semibold text-sm">
                            {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(day => (
                                <div key={day} className="text-gray-500">{day}</div>
                            ))}
                        </div>
                        <div className="grid grid-cols-7 gap-1 mt-2">
                            {leadingDays.map((_, i) => (
                                <div key={`leading-${i}`} className="p-2 text-gray-400"></div>
                            ))}
                            {daysInMonth.map((day, i) => {
                                const dayBookings = getBookingsForDay(day);
                                return (
                                    <div
                                        key={i}
                                        onClick={() => setSelectedDate(day)}
                                        className={`relative p-2 rounded-xl h-24 sm:h-32 overflow-hidden cursor-pointer transition-colors border
                                          ${isSameDay(day, selectedDate) ? 'bg-indigo-600 text-white font-bold border-indigo-600 shadow-lg' : 'bg-gray-50 hover:bg-gray-200 border-gray-200'}
                                          ${isToday(day) && !isSameDay(day, selectedDate) ? 'bg-indigo-200 text-indigo-800 border-indigo-300' : ''}
                                        `}
                                    >
                                        <span className={`text-lg font-bold mb-1 block ${isSameDay(day, selectedDate) ? 'text-white' : 'text-gray-800'}`}>
                                            {format(day, 'd')}
                                        </span>
                                        {dayBookings.length > 0 && (
                                            <div className="mt-1 space-y-1">
                                                {dayBookings.slice(0, 2).map((booking, index) => (
                                                    <div key={index} className={`truncate text-xs px-2 py-1 rounded-full ${isSameDay(day, selectedDate) ? 'bg-white text-indigo-600' : 'bg-indigo-500 text-white'
                                                        }`}>
                                                        <span className="flex items-center justify-center">
                                                            {booking.recurrenceId && <Repeat size={10} className="mr-1 flex-shrink-0" />}
                                                            <span className="truncate">{booking.activity}{booking.space ? ` (${booking.space})` : ''}</span>
                                                        </span>
                                                    </div>
                                                ))}
                                                {dayBookings.length > 2 && (
                                                    <span className={`text-xs px-2 py-1 rounded-full block text-center ${isSameDay(day, selectedDate) ? 'bg-white text-indigo-600' : 'bg-indigo-500 text-white'
                                                        }`}>
                                                        +{dayBookings.length - 2} mais...
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <h2 className="text-2xl font-bold text-gray-700 mb-4 flex items-center">
                        <Clock className="text-indigo-500 mr-2" />
                        Reservas para {format(selectedDate, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                    </h2>
                    {filteredBookings.length > 0 ? (
                        <ul className="space-y-4">
                            {filteredBookings.map((booking) => (
                                <li
                                    key={booking.id}
                                    className={`p-4 rounded-lg border shadow-sm transition-shadow ${isLoggedIn && booking.userId === userId ? 'bg-indigo-50 border-indigo-300 shadow-indigo-100' : 'bg-gray-50 border-gray-200'
                                        }`}
                                >
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <p className="font-bold text-gray-900 mb-1 flex items-center">
                                                <User size={18} className="mr-2 text-indigo-500" />
                                                {booking.name}
                                                {booking.recurrenceId && (
                                                    <span className="ml-2 px-2 py-0.5 bg-green-200 text-green-800 text-xs font-bold rounded-full flex items-center">
                                                        <Repeat size={12} className="mr-1" />
                                                        Recorrente
                                                    </span>
                                                )}
                                            </p>
                                            <p className="text-sm text-gray-700 mb-1 flex items-center">
                                                <Clipboard size={18} className="mr-2 text-indigo-500" />
                                                <span className="font-semibold">{booking.activity}</span>
                                            </p>
                                            {booking.space && (
                                                <p className="text-sm text-gray-700 mb-1 flex items-center">
                                                    <Home size={18} className="mr-2 text-indigo-500" />
                                                    <span>Espaço: <span className="font-semibold text-indigo-700">{booking.space}</span></span>
                                                </p>
                                            )}
                                            <p className="text-sm font-semibold text-gray-900">
                                                <Clock size={18} className="inline mr-2 text-indigo-500" />
                                                {format(booking.start, "dd 'de' MMMM 'às' HH:mm", { locale: ptBR })}
                                                {' - '}
                                                {format(booking.end, "dd 'de' MMMM 'às' HH:mm", { locale: ptBR })}
                                            </p>
                                        </div>
                                        {isLoggedIn && (booking.userId === userId || isAdmin) && (
                                            <div className="flex space-x-2">
                                                <button
                                                    onClick={() => openEditingModal(booking)}
                                                    className="p-2 text-white bg-indigo-500 rounded-md shadow hover:bg-indigo-600 transition-colors transform hover:scale-110"
                                                    aria-label="Editar reserva"
                                                >
                                                    <Edit size={20} />
                                                </button>
                                                <button
                                                    onClick={() => openDeleteModal(booking)}
                                                    className="p-2 text-white bg-red-500 rounded-md shadow hover:bg-red-600 transition-colors transform hover:scale-110"
                                                    aria-label="Cancelar reserva"
                                                >
                                                    <Trash2 size={20} />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-500 mt-2">
                                        Reservado por: <span className="font-mono text-gray-400 break-words">{booking.userEmail}</span>
                                    </p>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-gray-500">Nenhuma reserva encontrada para esta data. Agende a primeira!</p>
                    )}
                </div>

                {/* Formulário de Autenticação e Agendamento */}
                {isLoggedIn && (
                    <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
                        <h2 className="text-2xl font-bold text-gray-700 mb-4 flex items-center">
                            <CalendarRange className="text-indigo-500 mr-2" />
                            Nova Reserva
                        </h2>
                        <form onSubmit={handleBookingSubmit} className="space-y-4">
                            <div className="flex flex-col sm:flex-row gap-4">
                                <div className="flex-1 sm:w-5/12">
                                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                                        Nome do Responsável
                                    </label>
                                    <input
                                        type="text"
                                        id="name"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                                        placeholder="Seu nome"
                                        required
                                    />
                                </div>
                                <div className="flex-1 sm:w-4/12">
                                    <label htmlFor="activity" className="block text-sm font-medium text-gray-700 mb-1">
                                        Atividade
                                    </label>
                                    <input
                                        type="text"
                                        id="activity"
                                        value={activity}
                                        onChange={(e) => setActivity(e.target.value)}
                                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                                        placeholder="Ex: Reunião, estudo, evento"
                                        required
                                    />
                                </div>
                                <div className="w-full sm:w-44">
                                    <label htmlFor="space" className="block text-sm font-medium text-gray-700 mb-1">
                                        Espaço
                                    </label>
                                    <input
                                        type="text"
                                        id="space"
                                        value={space}
                                        onChange={(e) => setSpace(e.target.value)}
                                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                                        placeholder="Ex: Sala, Quarto 1"
                                        required
                                    />
                                </div>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-4">
                                <div className="flex-1">
                                    <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
                                        Início
                                    </label>
                                    <input
                                        type="datetime-local"
                                        id="startDate"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                                        required
                                    />
                                </div>
                                <div className="flex-1">
                                    <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
                                        Término
                                    </label>
                                    <input
                                        type="datetime-local"
                                        id="endDate"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                                        required
                                    />
                                </div>
                            </div>

                            {/* Campo de Recorrência */}
                            <div className="border border-indigo-200 bg-indigo-50 rounded-md p-3">
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="isRecurrent"
                                        checked={isRecurrent}
                                        onChange={(e) => setIsRecurrent(e.target.checked)}
                                        className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                    />
                                    <label htmlFor="isRecurrent" className="ml-2 block text-sm font-medium text-gray-700 flex items-center">
                                        <Repeat size={16} className="mr-1 text-indigo-500" />
                                        Agendamento Recorrente
                                    </label>
                                </div>
                                {isRecurrent && (
                                    <div className="mt-2">
                                        <label htmlFor="recurrenceType" className="block text-sm font-medium text-gray-700 mb-1">
                                            Repetir
                                        </label>
                                        <select
                                            id="recurrenceType"
                                            value={recurrenceType}
                                            onChange={(e) => setRecurrenceType(e.target.value)}
                                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-white"
                                        >
                                            <option value="Semanal">Semanalmente</option>
                                            <option value="Mensal">Mensalmente</option>
                                            <option value="Anual">Anualmente</option>
                                        </select>
                                    </div>
                                )}
                            </div>
                            {/* Fim do Campo de Recorrência */}

                            <button
                                type="submit"
                                className="w-full bg-indigo-600 text-white font-bold py-3 px-6 rounded-md shadow-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-transform transform hover:scale-105"
                            >
                                Agendar Apartamento
                            </button>
                        </form>
                    </div>
                )}
                {/* Modal / Pop-up de Login */}
                {isLoginModalOpen && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-xl p-8 max-w-sm w-full shadow-2xl border border-gray-200 relative">
                            <button
                                onClick={closeLoginModal}
                                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                                aria-label="Fechar"
                            >
                                <X size={20} />
                            </button>
                            <h2 className="text-2xl font-bold text-gray-700 mb-4 flex items-center">
                                <LogIn className="text-indigo-600 mr-2" />
                                Acesse para Agendar
                            </h2>
                            <form onSubmit={handleSignIn} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">E-mail</label>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                                        placeholder="seu.email@exemplo.com"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Senha</label>
                                    <input
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                                        placeholder="••••••••"
                                        required
                                    />
                                </div>
                                <div className="flex space-x-2 pt-2">
                                    <button
                                        type="button"
                                        onClick={closeLoginModal}
                                        className="w-1/2 py-2.5 px-4 bg-gray-200 text-gray-800 font-semibold rounded-md shadow hover:bg-gray-300 transition-colors"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        className="w-1/2 bg-indigo-600 text-white font-bold py-2.5 px-4 rounded-md shadow-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-transform transform hover:scale-105"
                                    >
                                        Entrar
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
                {isModalOpen && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-xl p-8 max-w-sm w-full shadow-2xl text-center border border-gray-200">
                            {messageType === 'success' ? (
                                <>
                                    <CheckCircle className="text-green-500 mx-auto mb-4" size={48} />
                                    <h3 className="text-xl font-bold mb-2 text-gray-800">Sucesso!</h3>
                                    <p className="text-gray-700 mb-6">{message}</p>
                                    <button
                                        onClick={closeModal}
                                        className="w-full bg-green-600 text-white font-bold py-2 rounded-md shadow hover:bg-green-700 transition-colors"
                                    >
                                        Continuar
                                    </button>
                                </>
                            ) : (
                                <>
                                    <AlertCircle className="text-red-500 mx-auto mb-4" size={48} />
                                    <h3 className="text-xl font-bold mb-2 text-gray-800">{messageType === 'error' ? 'Erro' : 'Atenção'}</h3>
                                    <p className="text-gray-700 mb-6">{message}</p>
                                    <button
                                        onClick={closeModal}
                                        className="w-full bg-red-600 text-white font-bold py-2 rounded-md shadow hover:bg-red-700 transition-colors"
                                    >
                                        Entendido
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {isEditingModalOpen && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-xl p-8 max-w-lg w-full shadow-2xl border border-gray-200">
                            <h3 className="text-2xl font-bold text-gray-700 mb-4">Editar Reserva</h3>
                            <form onSubmit={handleEditFormSubmit} className="space-y-4">
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <div className="flex-1 sm:w-5/12">
                                        <label htmlFor="editedName" className="block text-sm font-medium text-gray-700 mb-1">
                                            Nome do Responsável
                                        </label>
                                        <input
                                            type="text"
                                            id="editedName"
                                            value={editedName}
                                            onChange={(e) => setEditedName(e.target.value)}
                                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                                            required
                                        />
                                    </div>
                                    <div className="flex-1 sm:w-4/12">
                                        <label htmlFor="editedActivity" className="block text-sm font-medium text-gray-700 mb-1">
                                            Atividade
                                        </label>
                                        <input
                                            type="text"
                                            id="editedActivity"
                                            value={editedActivity}
                                            onChange={(e) => setEditedActivity(e.target.value)}
                                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                                            required
                                        />
                                    </div>
                                    <div className="w-full sm:w-44">
                                        <label htmlFor="editedSpace" className="block text-sm font-medium text-gray-700 mb-1">
                                            Espaço
                                        </label>
                                        <input
                                            type="text"
                                            id="editedSpace"
                                            value={editedSpace}
                                            onChange={(e) => setEditedSpace(e.target.value)}
                                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                                            placeholder="Ex: Sala, Quarto 1"
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <div className="flex-1">
                                        <label htmlFor="editedStartDate" className="block text-sm font-medium text-gray-700 mb-1">
                                            Início
                                        </label>
                                        <input
                                            type="datetime-local"
                                            id="editedStartDate"
                                            value={editedStartDate}
                                            onChange={(e) => setEditedStartDate(e.target.value)}
                                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                                            required
                                        />
                                    </div>
                                    <div className="flex-1">
                                        <label htmlFor="editedEndDate" className="block text-sm font-medium text-gray-700 mb-1">
                                            Término
                                        </label>
                                        <input
                                            type="datetime-local"
                                            id="editedEndDate"
                                            value={editedEndDate}
                                            onChange={(e) => setEditedEndDate(e.target.value)}
                                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="flex justify-end space-x-2">
                                    <button
                                        type="button"
                                        onClick={closeEditingModal}
                                        className="px-6 py-3 bg-gray-200 text-gray-800 font-bold rounded-md shadow hover:bg-gray-300 transition-colors"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-6 py-3 bg-indigo-600 text-white font-bold rounded-md shadow-lg hover:bg-indigo-700 transition-colors"
                                    >
                                        {editingBooking?.recurrenceId ? 'Continuar Edição' : 'Salvar Alterações'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Novo Modal de Ação de Edição */}
                {isEditActionModalOpen && editingBooking && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-xl p-8 max-w-sm w-full shadow-2xl text-center border border-gray-200">
                            <Edit className="text-indigo-500 mx-auto mb-4" size={48} />
                            <h3 className="text-xl font-bold mb-2 text-gray-700">Editar Recorrência</h3>
                            <p className="text-gray-700 mb-6">
                                Esta é uma reserva recorrente. Como deseja aplicar as alterações?
                            </p>
                            <div className="space-y-3">
                                <button
                                    onClick={() => handleFinalEditSubmit('single')}
                                    className="w-full bg-indigo-500 text-white font-bold py-2 rounded-md shadow hover:bg-indigo-600 transition-colors"
                                >
                                    Salvar Apenas Este Evento
                                </button>
                                <button
                                    onClick={() => handleFinalEditSubmit('future')}
                                    className="w-full bg-indigo-700 text-white font-bold py-2 rounded-md shadow hover:bg-indigo-800 transition-colors"
                                >
                                    Salvar Este e os Futuros
                                </button>
                            </div>
                            <button
                                onClick={closeEditingModal}
                                className="w-full mt-3 py-2 bg-gray-200 text-gray-800 font-bold rounded-md shadow hover:bg-gray-300 transition-colors"
                            >
                                Cancelar
                            </button>
                        </div>
                    </div>
                )}
                {/* Fim do Novo Modal de Ação de Edição */}

                {isDeleteModalOpen && bookingToDelete && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-xl p-8 max-w-sm w-full shadow-2xl text-center border border-gray-200">
                            <Trash2 className="text-red-500 mx-auto mb-4" size={48} />
                            <h3 className="text-xl font-bold mb-2 text-gray-700">Confirmar Exclusão</h3>
                            <p className="text-gray-700 mb-6">
                                Você está prestes a excluir a reserva de **{bookingToDelete.activity}** em {format(bookingToDelete.start, "dd/MM/yyyy")}.
                            </p>

                            {bookingToDelete.recurrenceId ? (
                                <div className="space-y-3">
                                    <p className="font-semibold text-sm text-red-600">Esta é uma reserva recorrente. Como deseja prosseguir?</p>
                                    <button
                                        onClick={() => handleRecurrenceDelete('single')}
                                        className="w-full bg-red-500 text-white font-bold py-2 rounded-md shadow hover:bg-red-600 transition-colors"
                                    >
                                        Excluir Apenas Este Evento
                                    </button>
                                    <button
                                        onClick={() => handleRecurrenceDelete('future')}
                                        className="w-full bg-red-700 text-white font-bold py-2 rounded-md shadow hover:bg-red-800 transition-colors"
                                    >
                                        Excluir Este e Todos os Futuros
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={() => handleRecurrenceDelete('single')}
                                    className="w-full bg-red-600 text-white font-bold py-2 rounded-md shadow hover:bg-red-700 transition-colors"
                                >
                                    Confirmar Exclusão
                                </button>
                            )}
                            <button
                                onClick={closeDeleteModal}
                                className="w-full mt-3 py-2 bg-gray-200 text-gray-800 font-bold rounded-md shadow hover:bg-gray-300 transition-colors"
                            >
                                Cancelar
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default App;
